import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import type { Json } from "@/integrations/supabase/types";

interface SettingRow {
  id: string;
  key: string;
  value: Json | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export function SettingsManagement() {
  const { user } = useAuth();
  const [settings, setSettings] = useState<SettingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(() => Number(localStorage.getItem("settings-page-size")) || 10);
  const setPageSize = (v: number) => { setPageSizeState(v); localStorage.setItem("settings-page-size", String(v)); };

  const [editDialog, setEditDialog] = useState<SettingRow | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [formKey, setFormKey] = useState("");
  const [formValue, setFormValue] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<SettingRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    const { data, count, error } = await supabase
      .from("settings")
      .select("*", { count: "exact" })
      .order("order_index")
      .range(from, to);
    if (!error && data) {
      setSettings(data as SettingRow[]);
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, pageSize]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const openCreate = () => {
    setIsNew(true);
    setFormKey("");
    setFormValue("");
    setFormErrors({});
    setEditDialog({} as SettingRow);
  };

  const openEdit = (s: SettingRow) => {
    setIsNew(false);
    setFormKey(s.key);
    setFormValue(s.value != null ? JSON.stringify(s.value, null, 2) : "");
    setFormErrors({});
    setEditDialog(s);
  };

  const [formErrors, setFormErrors] = useState<{ key?: boolean; value?: boolean }>({});

  const handleSave = async () => {
    const errors: { key?: boolean; value?: boolean } = {};
    if (!formKey.trim()) errors.key = true;
    let parsedValue: Json = null;
    if (formValue.trim()) {
      try {
        parsedValue = JSON.parse(formValue.trim());
      } catch {
        errors.value = true;
      }
    }
    if (errors.key || errors.value) {
      setFormErrors(errors);
      if (errors.key) toast.error("Clé requise");
      if (errors.value) toast.error("La valeur doit être du JSON valide");
      return;
    }
    setFormErrors({});
    setSaving(true);
    if (isNew) {
      const maxOrder = settings.length > 0 ? Math.max(...settings.map((s) => s.order_index)) + 1 : 0;
      const { error } = await supabase.from("settings").insert({
        key: formKey.trim(),
        value: parsedValue,
        order_index: maxOrder,
        created_by: user?.id,
        updated_by: user?.id,
      });
      if (error) { toast.error("Erreur lors de la création"); setSaving(false); return; }
      toast.success("Réglage créé");
    } else {
      const { error } = await supabase.from("settings").update({
        key: formKey.trim(),
        value: parsedValue,
        updated_by: user?.id,
      }).eq("id", editDialog!.id);
      if (error) { toast.error("Erreur lors de la modification"); setSaving(false); return; }
      toast.success("Réglage mis à jour");
    }
    setSaving(false);
    setEditDialog(null);
    fetchSettings();
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    const { error } = await supabase.from("settings").delete().eq("id", deleteDialog.id);
    if (error) { toast.error("Erreur lors de la suppression"); return; }
    toast.success("Réglage supprimé");
    setDeleteDialog(null);
    fetchSettings();
  };

  const handleDragStart = (id: string) => setDraggedId(id);
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = async (targetId: string) => {
    if (!draggedId || draggedId === targetId) { setDraggedId(null); return; }
    const oldIdx = settings.findIndex((s) => s.id === draggedId);
    const newIdx = settings.findIndex((s) => s.id === targetId);
    if (oldIdx < 0 || newIdx < 0) { setDraggedId(null); return; }
    const reordered = [...settings];
    const [moved] = reordered.splice(oldIdx, 1);
    reordered.splice(newIdx, 0, moved);
    setSettings(reordered);
    setDraggedId(null);
    await Promise.all(
      reordered.map((s, i) => supabase.from("settings").update({ order_index: i }).eq("id", s.id))
    );
    fetchSettings();
  };

  const formatValue = (v: Json | null): string => {
    if (v === null || v === undefined) return "—";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: "'DM Sans', sans-serif" }}>Réglages</CardTitle>
        <Button size="sm" variant="outline" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Nouveau réglage
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : settings.length === 0 ? (
          <p className="text-muted-foreground">Aucun réglage trouvé.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Clé</TableHead>
                  <TableHead>Valeur</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {settings.map((setting) => (
                  <TableRow
                    key={setting.id}
                    draggable
                    onDragStart={() => handleDragStart(setting.id)}
                    onDragOver={handleDragOver}
                    onDrop={() => handleDrop(setting.id)}
                    className={draggedId === setting.id ? "opacity-50" : ""}
                  >
                    <TableCell>
                      <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
                    </TableCell>
                    <TableCell className="font-medium font-mono text-sm">{setting.key}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded max-w-xs truncate block">
                        {formatValue(setting.value)}
                      </code>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(setting)} title="Modifier">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setDeleteDialog(setting)} title="Supprimer" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Lignes par page :</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-[70px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span>{total} résultat{total > 1 ? "s" : ""}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">{page} / {totalPages}</span>
                <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isNew ? "Nouveau réglage" : "Modifier le réglage"}</DialogTitle>
            <DialogDescription>{isNew ? "Ajoutez un nouveau paramètre de configuration." : `Modification de « ${editDialog?.key ?? ""} »`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Clé *</Label>
              <Input value={formKey} onChange={(e) => { setFormKey(e.target.value); setFormErrors((f) => ({ ...f, key: false })); }} placeholder="ma_cle" className={`font-mono ${formErrors.key ? "border-destructive ring-1 ring-destructive" : ""}`} />
            </div>
            <div className="space-y-2">
              <Label>Valeur (JSON)</Label>
              <Textarea
                value={formValue}
                onChange={(e) => { setFormValue(e.target.value); setFormErrors((f) => ({ ...f, value: false })); }}
                placeholder='{"key": "value"}'
                className={`font-mono text-sm min-h-[120px] ${formErrors.value ? "border-destructive ring-1 ring-destructive" : ""}`}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Annuler</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce réglage ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le réglage « {deleteDialog?.key} » sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
